// src/components/assets/EditAssetModal.tsx
// Modal for editing tracked assets
// Created: 2025-01-31

import React, { useState, useEffect } from 'react';
import { X, Edit } from 'lucide-react';
import { AssetCategory, TrackedAsset, UpdateAssetRequest, FundingSource, FUNDING_SOURCE_LABELS } from '../../types/asset';

interface EditAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (assetData: UpdateAssetRequest) => void;
  asset: TrackedAsset;
  categories: AssetCategory[];
}

const EditAssetModal: React.FC<EditAssetModalProps> = ({ isOpen, onClose, onSave, asset, categories }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    location: '',
    purchaseDate: '',
    purchaseValue: '',
    currentValue: '',
    fundingSource: '',
    grantId: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && asset) {
      setFormData({
        name: asset.name || '',
        description: asset.description || '',
        categoryId: asset.categoryId || '',
        location: asset.location || '',
        purchaseDate: asset.purchaseDate || '',
        purchaseValue: asset.purchaseValue?.toString() || '',
        currentValue: asset.currentValue?.toString() || '',
        fundingSource: asset.fundingSource || '',
        grantId: asset.grantId || '',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        serialNumber: asset.serialNumber || '',
        notes: asset.notes || ''
      });
    }
  }, [isOpen, asset]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.categoryId) newErrors.categoryId = 'Category is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if ((formData.fundingSource === 'federal_grant' || formData.fundingSource === 'state_grant') && !formData.grantId.trim()) {
      newErrors.grantId = 'Grant ID required for grant-funded items';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const submitData: UpdateAssetRequest = {
      name: formData.name,
      categoryId: formData.categoryId,
      location: formData.location,
      description: formData.description || undefined,
      purchaseDate: formData.purchaseDate || undefined,
      purchaseValue: formData.purchaseValue ? parseFloat(formData.purchaseValue) : undefined,
      currentValue: formData.currentValue ? parseFloat(formData.currentValue) : undefined,
      fundingSource: formData.fundingSource ? (formData.fundingSource as FundingSource) : undefined,
      grantId: formData.grantId || undefined,
      manufacturer: formData.manufacturer || undefined,
      model: formData.model || undefined,
      serialNumber: formData.serialNumber || undefined,
      notes: formData.notes || undefined
    };
    onSave(submitData);
  };

  if (!isOpen) return null;

  const showGrantField = formData.fundingSource === 'federal_grant' || formData.fundingSource === 'state_grant';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <Edit className="w-6 h-6 text-blue-600 mr-2" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Edit Asset</h2>
              <p className="text-sm text-gray-500 font-mono">{asset.assetTag}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Identification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asset Tag</label>
                <input type="text" value={asset.assetTag} disabled className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500" />
                <p className="text-xs text-gray-500 mt-1">Asset tag cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                <select name="categoryId" value={formData.categoryId} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.categoryId ? 'border-red-500' : 'border-gray-300'}`}>
                  <option value="">Select category...</option>
                  {categories.map(cat => (<option key={cat.categoryId} value={cat.categoryId}>{cat.name}</option>))}
                </select>
                {errors.categoryId && <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Location & Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location <span className="text-red-500">*</span></label>
                <input type="text" name="location" value={formData.location} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.location ? 'border-red-500' : 'border-gray-300'}`} />
                {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input type="text" name="model" value={formData.model} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Financial Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Value</label>
                <input type="number" name="purchaseValue" value={formData.purchaseValue} onChange={handleInputChange} step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Value</label>
                <input type="number" name="currentValue" value={formData.currentValue} onChange={handleInputChange} step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Funding Source</label>
                <select name="fundingSource" value={formData.fundingSource} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">Select funding source...</option>
                  {Object.entries(FUNDING_SOURCE_LABELS).map(([key, label]) => (<option key={key} value={key}>{label}</option>))}
                </select>
              </div>
              {showGrantField && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grant ID <span className="text-red-500">*</span></label>
                  <input type="text" name="grantId" value={formData.grantId} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.grantId ? 'border-red-500' : 'border-gray-300'}`} />
                  {errors.grantId && <p className="text-red-500 text-xs mt-1">{errors.grantId}</p>}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
            <button type="submit" className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#082759' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0a2e6b'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#082759'}>Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAssetModal;
